import { X } from "@icons";
import * as Dialog from "@radix-ui/react-dialog";
import { useRestoreFocusOnClose } from "@src/hooks/use-restore-focus-on-close";
import classNames from "classnames";
import { RefObject, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useCleanupAfterRadix } from "../../hooks/helperHooks";
import { DialogLiveRegions } from "../live-announcer";

export type CloseButtonVariant = "rennala" | "legacy";

const CLOSE_BUTTON_VARIANT_CLASSES: Record<CloseButtonVariant, string> = {
  rennala:
    "text-muted hover:bg-surface focus:outline-none focus:ring-2 focus:ring-focus",
  legacy: "",
};

interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  children?: React.ReactNode;
  openTriggerChild?: React.ReactNode;
  triggerAsChild?: boolean;
  closeButtonVariant?: CloseButtonVariant;
  size?: "sm" | "md" | "lg";
  hideTitle?: boolean;
  hideDescription?: boolean;
  alert?: { leastDestructiveRef: RefObject<HTMLElement> };
  returnFocusRef?: RefObject<HTMLElement | null>;
  focusContentOnOpen?: boolean;
  onCloseAutoFocus?: (event: Event) => void;
}

export default function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
  openTriggerChild,
  triggerAsChild = false,
  closeButtonVariant = "rennala",
  size = "md",
  hideTitle = false,
  hideDescription = false,
  alert,
  returnFocusRef,
  focusContentOnOpen = false,
  onCloseAutoFocus,
}: ModalProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  useCleanupAfterRadix(open);
  const restoreFocusOnClose = useRestoreFocusOnClose(
    open,
    Boolean(openTriggerChild),
    returnFocusRef,
  );

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      {openTriggerChild && (
        <Dialog.Trigger className="rounded" asChild={triggerAsChild}>
          {openTriggerChild}
        </Dialog.Trigger>
      )}
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[60] bg-scrim bg-opacity-50" />
        <Dialog.Content
          ref={contentRef}
          role={alert ? "alertdialog" : "dialog"}
          className={classNames(
            "fixed z-[60] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-page rounded-md max-h-[80%] w-[90%] flex flex-col",
            {
              "max-w-[400px]": size === "sm",
              "max-w-[600px]": size === "md",
              "max-w-[800px]": size === "lg",
            },
          )}
          onInteractOutside={(e) => e.preventDefault()}
          onOpenAutoFocus={(event) => {
            if (alert) {
              const leastDestructive = alert.leastDestructiveRef.current;
              if (!leastDestructive) return;
              event.preventDefault();
              leastDestructive.focus();
              return;
            }
            if (!focusContentOnOpen) return;
            event.preventDefault();
            contentRef.current?.focus();
          }}
          onCloseAutoFocus={(event) => {
            onCloseAutoFocus?.(event);
            if (event.defaultPrevented) return;
            restoreFocusOnClose(event);
          }}
        >
          <div className="flex-shrink-0 p-4 pb-0">
            <Dialog.Title
              className={classNames(
                "font-semibold text-xl my-2 pr-11 break-words text-primary",
                {
                  "sr-only": hideTitle,
                },
              )}
            >
              {title}
            </Dialog.Title>
            <Dialog.Description
              className={classNames("text-default mb-4", {
                "sr-only": hideDescription,
              })}
            >
              {description}
            </Dialog.Description>
          </div>
          <div className="flex-1 overflow-auto p-4 pt-0">{children}</div>
          <Dialog.Close
            aria-label={t("close")}
            className={classNames(
              "absolute top-3 right-3 rounded-full w-target h-target flex justify-center items-center bg-page",
              CLOSE_BUTTON_VARIANT_CLASSES[closeButtonVariant],
            )}
          >
            <X size={18} />
          </Dialog.Close>
          <DialogLiveRegions open={open} />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
